import { createContext, useContext, useMemo, useCallback, useEffect } from "react";
import { useArboData, isRecurrenteGhost } from "../hooks/useMonthData";
import { useGlobalData } from "../hooks/useGlobalData";
import { useNavigation } from "./NavigationContext";
import { sum, sumFijosReal, fmt } from "../utils/format";
import { getMsiCuotasMes } from "../utils/gastos";
import { calcularSaldo } from "../utils/saldo";
import { MESES, MESES_CORTO } from "../constants/months";
import { P } from "../styles/tokens";
import { reportar } from "../utils/reportar";

const FinancialContext = createContext(null);

export function FinancialProvider({ children }) {
  const { activeMonth } = useNavigation();
  const currentMonth = new Date().getMonth();
  const year = new Date().getFullYear();
  const today = new Date();

  /* ── Datos del año (12 meses) desde API ── */
  const {
    data, setData, month, setMonth, saveItem, mesId, mesIds,
    loading: monthLoading, error: monthError, refetch: refetchMonths,
  } = useArboData(activeMonth);

  /* ── Datos globales ── */
  const {
    metas, setMetas,
    presupuestos, setPresupuestos,
    recurrentes, setRecurrentes,
    msiCompras, setMsiCompras: _setMsiRaw, createMsiCompra, deleteMsiCompra, toggleMsiPago,
    cuentas, createCuenta, updateCuenta, deleteCuenta,
    categorias, createCategoria, updateCategoria, deleteCategoria,
    transferencias, setTransferencias,
    diaCobro, setDiaCobro,
    nombreUsuario, setNombreUsuario,
    onboardingCompleted, setOnboardingCompleted,
    loading: globalLoading, error: globalError, refetch: refetchGlobal, refetchTransferencias,
  } = useGlobalData();

  /* ── MSI: wrapper para detectar add/delete ── */
  const setMsiCompras = useCallback((v) => {
    const curr = msiCompras;
    const next = typeof v === "function" ? v(curr) : v;
    const deleted = curr.filter(p => !next.find(n => n.id === p.id));
    if (deleted.length > 0) { deleted.forEach(d => deleteMsiCompra(d.id).catch(reportar("borrar compra MSI"))); return; }
    const created = next.filter(n => !curr.find(p => p.id === n.id));
    if (created.length > 0) {
      created.forEach(c => {
        const payload = {
          concepto: c.concepto || "Compra a meses",
          categoria: c.cat || "Otros",
          total_compra: Number(c.totalCompra) || 0,
          num_meses: Number(c.meses) || 12,
          mes_inicio: (Number(c.mesInicio) || 0) + 1,
          anio_inicio: Number(c.anioInicio) || new Date().getFullYear(),
          cuenta_id: c.cuentaId || null,
        };
        createMsiCompra(payload).catch(reportar("crear compra MSI"));
      });
      return;
    }
    _setMsiRaw(next);
  }, [msiCompras, createMsiCompra, deleteMsiCompra, _setMsiRaw]);

  /* ── Sincronizar recurrentes en todos los meses (vista local) ── */
  useEffect(() => {
    if (!recurrentes.length) return;
    // Dedupe por id para evitar ghosts duplicados si el array trae el mismo recurrente 2 veces
    const uniqRecurrentes = recurrentes.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
    setData(prev => {
      const next = { ...prev };
      // Crear lookup de recurrentes por ID para actualizar reales no pagados
      const recMap = {};
      uniqRecurrentes.forEach(r => { recMap[r.id] = r; });

      for (let i = 0; i < 12; i++) {
        const m = next[i];
        // Actualizar fijos/extras reales NO pagados si su recurrente cambió
        const fijosReales = m.fijos.filter(f => !String(f.id).startsWith("rec_")).map(f => {
          if (f.recurrenteId && !f.pagado && recMap[f.recurrenteId]) {
            const r = recMap[f.recurrenteId];
            return { ...f, concepto: r.concepto, monto: r.monto, fechaLimite: r.dia_mes ? String(r.dia_mes) : f.fechaLimite, cuentaId: r.cuenta_id || f.cuentaId };
          }
          return f;
        });
        const extrasReales = m.extras.filter(e => !String(e.id).startsWith("rec_")).map(e => {
          if (e.recurrenteId && recMap[e.recurrenteId]) {
            const r = recMap[e.recurrenteId];
            return { ...e, concepto: r.concepto, monto: r.monto, cat: r.cat || e.cat, cuentaId: r.cuenta_id || e.cuentaId };
          }
          return e;
        });
        const ingresosReales = m.ingresos.filter(ing => !String(ing.id).startsWith("rec_"));
        const realFijoRecIds = new Set(fijosReales.filter(f => f.recurrenteId).map(f => f.recurrenteId));
        const realExtraRecIds = new Set(extrasReales.filter(e => e.recurrenteId).map(e => e.recurrenteId));
        const realIngresoRecIds = new Set(ingresosReales.filter(ing => ing.recurrenteId).map(ing => ing.recurrenteId));
        next[i] = {
          ...m,
          // Recurrentes ghost: solo desde el mes actual en adelante (no generar deuda en meses pasados)
          fijos: [
            ...fijosReales,
            ...(i >= currentMonth ? recurrentes.filter(r => r.tipo === "fijo" && !realFijoRecIds.has(r.id))
              .map(r => ({ id: `rec_${r.id}`, recurrenteId: r.id, concepto: r.concepto, monto: r.monto, esRecurrente: true, fechaLimite: r.dia_mes ? String(r.dia_mes) : "", pagado: false, montoPagado: null, cuentaId: r.cuenta_id || null })) : []),
          ],
          extras: [
            ...extrasReales,
            ...(i >= currentMonth ? recurrentes.filter(r => r.tipo === "extra" && !realExtraRecIds.has(r.id))
              .map(r => ({ id: `rec_${r.id}`, recurrenteId: r.id, concepto: r.concepto, monto: r.monto, cat: r.cat, esRecurrente: true, msi: null, cuentaId: r.cuenta_id || null })) : []),
          ],
          ingresos: [
            ...ingresosReales,
            ...(i >= currentMonth ? recurrentes.filter(r => r.tipo === "ingreso" && !realIngresoRecIds.has(r.id))
              .map(r => ({ id: `rec_${r.id}`, recurrenteId: r.id, concepto: r.concepto, monto: r.monto, esRecurrente: true, cuentaId: r.cuenta_id || null, fecha: null, orden: 0 })) : []),
          ],
        };
      }
      return next;
    });
  }, [recurrentes, currentMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Saldos de cuentas ── */
  const _tracked = cuentas.filter(c => c.rastrear_saldo);

  const computedSaldos = useMemo(() => {
    const saldos = {};
    for (const cuenta of _tracked) {
      const cid = cuenta.id;
      // Ventana de cálculo: año en curso hasta el mes actual. Los meses futuros
      // no afectan el saldo real. El backend replica esta misma ventana.
      const ingresos = [], extras = [], fijos = [];
      for (let m = 0; m <= currentMonth; m++) {
        const md = data[m];
        if (!md) continue;
        for (const i of md.ingresos) if (i.cuentaId === cid) ingresos.push(i);
        for (const e of md.extras)   if (e.cuentaId === cid) extras.push(e);
        for (const f of md.fijos)    if (f.cuentaId === cid) fijos.push({ ...f, monto_pagado: f.montoPagado });
      }

      let transferEntrante = 0, transferSaliente = 0;
      for (const t of (transferencias || [])) {
        const monto = Number(t.monto) || 0;
        if (t.cuenta_destino === cid) transferEntrante += monto;
        if (t.cuenta_origen === cid) transferSaliente += monto;
      }

      let cuotasMsi = 0;
      if (cuenta.tipo === "crédito") {
        for (let m = 0; m <= currentMonth; m++) {
          cuotasMsi += getMsiCuotasMes(m, msiCompras, year)
            .filter(c => c.cuentaId === cid)
            .reduce((s, c) => s + (Number(c.cuota) || 0), 0);
        }
      }

      // La aritmética vive en utils/saldo.js, gemelo de backend/app/core/saldo.py.
      // Los ghosts (id "rec_*") se excluyen ahí dentro.
      saldos[cid] = calcularSaldo({
        tipo: cuenta.tipo,
        saldoInicial: cuenta.saldo_inicial,
        ingresos, extras, fijos,
        transferEntrante, transferSaliente, cuotasMsi,
      });
    }
    return saldos;
  }, [_tracked, data, transferencias, currentMonth, msiCompras, year]);

  /* ── Auto-pago de fijos TDC al llegar la fecha límite (si hay crédito disponible) ── */
  useEffect(() => {
    if (activeMonth !== currentMonth) return;
    const tdcIds = new Set(cuentas.filter(c => c.tipo === "crédito").map(c => c.id));
    if (!tdcIds.size) return;
    const md = data[currentMonth];
    if (!md || !md.fijos?.length) return;
    const todayDay = new Date().getDate();

    const candidatos = md.fijos.filter(f => {
      if (f.pagado) return false;
      if (!f.cuentaId || !tdcIds.has(f.cuentaId)) return false;
      const dia = Number(f.fechaLimite);
      if (!dia || todayDay < dia) return false;
      return (Number(f.monto) || 0) > 0;
    });
    if (!candidatos.length) return;

    // Orden: fecha más vieja primero, luego monto menor (maximiza cuántos caben)
    candidatos.sort((a, b) =>
      (Number(a.fechaLimite) - Number(b.fechaLimite)) ||
      (Number(a.monto) - Number(b.monto))
    );

    // Simular cargos acumulados por TDC contra el límite de crédito
    const saldosSim = {};
    tdcIds.forEach(id => { saldosSim[id] = computedSaldos[id] || 0; });
    const toPay = new Set();
    for (const f of candidatos) {
      const cta = cuentas.find(c => c.id === f.cuentaId);
      const limite = Number(cta?.limite_credito) || 0;
      if (!limite) continue; // sin límite definido, no se puede validar
      const monto = Number(f.monto) || 0;
      if (saldosSim[f.cuentaId] + monto <= limite) {
        toPay.add(f.id);
        saldosSim[f.cuentaId] += monto;
      }
    }
    if (!toPay.size) return;

    setMonth(prev => ({
      ...prev,
      fijos: prev.fijos.map(f => toPay.has(f.id)
        ? { ...f, pagado: true, montoPagado: Number(f.monto) || 0 }
        : f
      ),
    }));
  }, [activeMonth, currentMonth, data, cuentas, computedSaldos, setMonth]);

  const saldoEfectivo = _tracked.filter(c => c.tipo === "efectivo").reduce((s, c) => s + (computedSaldos[c.id] || 0), 0);
  const saldoDebito = _tracked.filter(c => c.tipo === "débito").reduce((s, c) => s + (computedSaldos[c.id] || 0), 0);
  const saldoVales = _tracked.filter(c => c.tipo === "vales").reduce((s, c) => s + (computedSaldos[c.id] || 0), 0);
  const saldoAhorro = _tracked.filter(c => c.tipo === "ahorro").reduce((s, c) => s + (computedSaldos[c.id] || 0), 0);
  const capitalDisponible = saldoEfectivo + saldoDebito + saldoVales;
  const hasCuentas = _tracked.length > 0;

  /* ── IDs de cuentas crédito ── */
  const creditoIds = useMemo(() => new Set(cuentas.filter(c => c.tipo === "crédito").map(c => c.id)), [cuentas]);

  /* ── Valores derivados del mes activo ── */
  const cuotasMes = getMsiCuotasMes(activeMonth, msiCompras, year);
  const totalMsiMes = cuotasMes.reduce((s, c) => s + c.cuota, 0);
  const totalIngBase = sum(month.ingresos);
  const totalIng = totalIngBase;
  const totalFij = sumFijosReal(month.fijos);
  const totalExt = sum(month.extras) + totalMsiMes;

  const extrasTDC = month.extras.filter(e => e.cuentaId && creditoIds.has(e.cuentaId));
  const fijosTDC = month.fijos.filter(f => f.cuentaId && creditoIds.has(f.cuentaId));
  const totalTDC = sum(extrasTDC) + sumFijosReal(fijosTDC) + totalMsiMes;
  // MSI cuotas de TDC ya se contabilizan en deudaTDC — excluir de totalExtSinTDC
  const msiTDC = cuotasMes.filter(c => c.cuentaId && creditoIds.has(c.cuentaId)).reduce((s, c) => s + c.cuota, 0);
  const totalExtSinTDC = totalExt - sum(extrasTDC) - msiTDC;
  const totalFijSinTDC = totalFij - sumFijosReal(fijosTDC);

  const flujo = totalIng - totalFijSinTDC - totalExtSinTDC;
  const ahorroReal = (month.ahorroMetas || []).reduce((s, a) => s + (a.monto || 0), 0);
  const capitalReal = capitalDisponible - ahorroReal;
  const flujoNeto = flujo - ahorroReal;

  /* ── Deuda TDC por pagar (periodo de facturación real) ── */
  const deudaTDC = useMemo(() => {
    const tdcCuentas = cuentas.filter(c => c.tipo === "crédito");
    if (!tdcCuentas.length) return [];
    const todayDay = today.getDate();
    const mesAnterior = activeMonth > 0 ? data[activeMonth - 1] : null;

    return tdcCuentas.map(tdc => {
      const corte = Number(tdc.fecha_corte) || 0;

      // Periodo de facturación:
      // Sin corte → todos los extras del mes actual
      // Con corte → (corte+1 del mes anterior) hasta (corte del mes actual)
      let gastosExtras = [];
      if (!corte) {
        // Sin fecha de corte: todos los del mes actual
        gastosExtras = month.extras.filter(e => e.cuentaId === tdc.id);
      } else {
        // Extras del mes anterior con fecha > corte (post-corte del mes pasado)
        if (mesAnterior) {
          const prevExtras = mesAnterior.extras.filter(e => {
            if (e.cuentaId !== tdc.id || !e.fecha) return false;
            return new Date(e.fecha + "T00:00:00").getDate() > corte;
          });
          gastosExtras.push(...prevExtras);
        }
        // Extras del mes actual con fecha <= corte
        const currExtras = month.extras.filter(e => {
          if (e.cuentaId !== tdc.id) return false;
          if (!e.fecha) return true; // sin fecha → incluir en periodo actual
          return new Date(e.fecha + "T00:00:00").getDate() <= corte;
        });
        gastosExtras.push(...currExtras);
      }

      // Fijos: siempre del mes actual (no tienen fecha diaria precisa)
      const gastosFijos = month.fijos.filter(f => f.cuentaId === tdc.id);
      const msiCuotasTDC = cuotasMes.filter(c => c.cuentaId === tdc.id);
      const totalExtras = gastosExtras.reduce((s, e) => s + (Number(e.monto) || 0), 0);
      // Fijos: sumar el monto real cargado a la TDC (pagado usa montoPagado si existe, si no estimado)
      const totalFijos = gastosFijos.reduce((s, f) => {
        const val = f.pagado ? (Number(f.montoPagado) || Number(f.monto) || 0) : (Number(f.monto) || 0);
        return s + val;
      }, 0);
      const totalMsi = msiCuotasTDC.reduce((s, c) => s + (Number(c.cuota) || 0), 0);

      // Gastos post-corte del mes actual (pertenecen al SIGUIENTE ciclo de pago)
      const gastosPostCorte = corte ? month.extras.filter(e => {
        if (e.cuentaId !== tdc.id || !e.fecha) return false;
        return new Date(e.fecha + "T00:00:00").getDate() > corte;
      }).reduce((s, e) => s + (e.monto || 0), 0) : 0;

      const totalDeudaBruta = totalExtras + totalFijos + totalMsi;

      // Pagos hechos a esta TDC en el mes activo
      const pagosHechos = (transferencias || [])
        .filter(t => {
          if (t.cuenta_destino !== tdc.id) return false;
          if (!t.created_at) return false;
          const d = new Date(t.created_at);
          return d.getMonth() === activeMonth && d.getFullYear() === year;
        })
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);
      const totalDeudaMes = Math.max(totalDeudaBruta - pagosHechos, 0);

      // Distribuir pagos por concepto de la transferencia
      const pagosDelMes = (transferencias || []).filter(t => {
        if (t.cuenta_destino !== tdc.id || !t.created_at) return false;
        const d = new Date(t.created_at);
        return d.getMonth() === activeMonth && d.getFullYear() === year;
      });
      const pagoGastos = pagosDelMes.filter(t => t.concepto === "Pago TDC - Gastos" || t.concepto === "Pago TDC")
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);
      const pagoMsi = pagosDelMes.filter(t => t.concepto === "Pago TDC - MSI" || t.concepto === "Pago TDC")
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);

      // Para "Pago TDC" (todo junto), distribuir proporcionalmente
      const pagoTodo = pagosDelMes.filter(t => t.concepto === "Pago TDC")
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);
      const pagoSoloGastos = pagosDelMes.filter(t => t.concepto === "Pago TDC - Gastos")
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);
      const pagoSoloMsi = pagosDelMes.filter(t => t.concepto === "Pago TDC - MSI")
        .reduce((s, t) => s + (Number(t.monto) || 0), 0);

      const gastosNormalesBruto = totalExtras + totalFijos;
      // Pago "todo" se distribuye: primero gastos normales, luego MSI
      let todoParaGastos = Math.min(pagoTodo, gastosNormalesBruto);
      let todoParaMsi = pagoTodo - todoParaGastos;

      const gastosNormalesPendiente = Math.max(gastosNormalesBruto - pagoSoloGastos - todoParaGastos, 0);
      const msiPendiente = Math.max(totalMsi - pagoSoloMsi - todoParaMsi, 0);

      // totalDeuda = lo que realmente debes a la TDC hoy (saldo acumulado real).
      // Incluye: saldo_inicial (deuda preexistente), extras, fijos pagados, MSI ya cargados,
      // menos pagos (transferencias hacia la TDC). Es la fuente de verdad para "cuánto debes".
      const saldoTDC = computedSaldos[tdc.id] || 0;
      const deudaAcumulada = Math.max(saldoTDC, 0);
      const totalDeuda = deudaAcumulada;

      return {
        id: tdc.id, nombre: tdc.nombre, banco: tdc.banco,
        fechaLimitePago: tdc.fecha_limite_pago, fechaCorte: corte || null,
        totalDeuda, totalDeudaMes, totalDeudaBruta, deudaAcumulada,
        pagosHechos, gastosPostCorte,
        // Desglose para pago selectivo (neto, ya descontando pagos previos)
        totalExtras, totalFijos, totalMsi,
        gastosNormales: gastosNormalesPendiente,
        msiPendiente,
        nGastos: gastosExtras.length + gastosFijos.length,
        nMsi: msiCuotasTDC.length, limiteCredito: Number(tdc.limite_credito) || 0,
        pagada: totalDeuda <= 0,
      };
    }).filter(t => t.totalDeuda > 0 || t.gastosPostCorte > 0);
  }, [cuentas, month.extras, month.fijos, cuotasMes, transferencias, activeMonth, year, today, computedSaldos, data]);
  const totalDeudaTDCGlobal = deudaTDC.reduce((s, t) => s + t.totalDeuda, 0);

  /* ── Dinero comprometido vs libre ── */
  const comprometido = useMemo(() => {
    // 1. TDC acumulada (ya calculada)
    const tdcPendiente = totalDeudaTDCGlobal;

    // 2. Gastos fijos NO pagados (incluye recurrentes — son obligaciones reales; excluye TDC)
    const fijosSinPagar = month.fijos
      .filter(f => !f.pagado && f.monto > 0 && !(f.cuentaId && creditoIds.has(f.cuentaId)))
      .reduce((s, f) => s + (Number(f.monto) || 0), 0);

    // 3. MSI del mes (cuotas pendientes)
    const msiPendiente = totalMsiMes;

    // 4. Ahorro asignado
    const ahorroPendiente = ahorroReal;

    const totalComprometido = tdcPendiente + fijosSinPagar + ahorroPendiente;
    // No sumar MSI por separado si ya está en totalDeudaTDCGlobal (evitar doble conteo)
    // MSI ya está incluido en totalDeudaTDCGlobal para TDC, pero MSI sin TDC no

    const dineroLibre = capitalDisponible - totalComprometido;

    return {
      tdcPendiente,
      fijosSinPagar,
      msiPendiente,
      ahorroPendiente,
      totalComprometido,
      dineroLibre,
    };
  }, [totalDeudaTDCGlobal, month.fijos, totalMsiMes, ahorroReal, capitalDisponible]);

  /* ── Alerta TDC: solo si deuda pendiente (no pagada) y flujo < 20% de lo adeudado ── */
  const deudaTDCSinPagar = deudaTDC.filter(t => !t.pagada);
  const totalDeudaSinPagar = deudaTDCSinPagar.reduce((s, t) => s + t.totalDeuda, 0);

  const alertaTDC = useMemo(() => {
    if (!deudaTDCSinPagar.length || totalDeudaSinPagar === 0) return null;

    const disponible = saldoEfectivo + saldoDebito;
    const despuesDePagar = disponible - totalDeudaSinPagar;
    const pctRestante = totalIngBase > 0 ? (despuesDePagar / totalIngBase) * 100 : 0;

    // Calcular días restantes hasta fecha límite de pago más próxima
    const todayDay = today.getDate();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    let diasHastaLimite = null;
    let fechaLimiteMasCercana = null;

    for (const tdc of deudaTDCSinPagar) {
      if (tdc.fechaLimitePago) {
        const limDay = Number(tdc.fechaLimitePago);
        let dias;
        if (limDay >= todayDay) {
          dias = limDay - todayDay;
        } else {
          dias = (daysInMonth - todayDay) + limDay;
        }
        if (diasHastaLimite === null || dias < diasHastaLimite) {
          diasHastaLimite = dias;
          fechaLimiteMasCercana = limDay;
        }
      }
    }

    if (despuesDePagar < 0) {
      return {
        nivel: "critico",
        disponible,
        despuesDePagar,
        pctRestante,
        diasHastaLimite,
        fechaLimiteMasCercana,
        faltante: totalDeudaSinPagar - disponible,
      };
    }

    if (pctRestante < 20) {
      return {
        nivel: "alerta",
        disponible,
        despuesDePagar,
        pctRestante,
        diasHastaLimite,
        fechaLimiteMasCercana,
        faltante: 0,
      };
    }

    return null;
  }, [deudaTDCSinPagar, totalDeudaSinPagar, saldoEfectivo, saldoDebito, totalIngBase, today]);

  /* ── Datos anuales ── */
  const anualData = MESES.map((_, i) => {
    const m = data[i]; const ing = sum(m.ingresos), fij = sumFijosReal(m.fijos), ext = sum(m.extras);
    const ahMes = (m.ahorroMetas || []).reduce((s, a) => s + (a.monto || 0), 0);
    return { mes: MESES_CORTO[i], ingresos: ing, fijos: fij, extras: ext, ahorro: ahMes };
  });
  const anualTotals = anualData.reduce((a, m) => ({ ingresos: a.ingresos + m.ingresos, gastos: a.gastos + m.fijos + m.extras, ahorro: a.ahorro + m.ahorro }), { ingresos: 0, gastos: 0, ahorro: 0 });

  /* ── Pie chart data ── */
  const pieData = [
    { name: "Fijos", value: totalFij, color: "#1E293B" },
    { name: "Extras", value: totalExt, color: "#C65D3B" },
    { name: "Ahorro", value: ahorroReal, color: "#143D2E" },
    { name: "Disponible", value: Math.max(flujo - ahorroReal, 0), color: "#D97706" },
  ].filter(d => d.value > 0);

  /* ── Flujo disponible centralizado (solo descuenta pagados) ── */
  const fijosPagadosSinTDC = month.fijos
    .filter(f => f.pagado && !(f.cuentaId && creditoIds.has(f.cuentaId)))
    .reduce((s, f) => s + (f.montoPagado != null ? Number(f.montoPagado) : Number(f.monto) || 0), 0);
  const extrasRealesSinTDC = month.extras
    .filter(e => !isRecurrenteGhost(e) && !(e.cuentaId && creditoIds.has(e.cuentaId)))
    .reduce((s, e) => s + (Number(e.monto) || 0), 0);
  const flujoDisponible = totalIng - fijosPagadosSinTDC - extrasRealesSinTDC;

  /* ── Indicadores de salud financiera ── */
  const saludToken = flujoNeto > 0 ? P.ingresos : flujoNeto === 0 ? P.warn : P.danger;
  const saludLabel = flujoNeto > 0 ? "✓ Flujo positivo" : flujoNeto === 0 ? "Sin flujo" : "⚠ Déficit";
  const pctFlujo = totalIng > 0 ? (flujoNeto / totalIng) * 100 : 0;

  const alertCount = useMemo(() => {
    const bycat = {};
    month.extras.forEach(e => { if (e.cat) bycat[e.cat] = (bycat[e.cat] || 0) + (e.monto || 0); });
    return presupuestos.filter(p => p.cat && p.limite > 0 && (bycat[p.cat] || 0) / p.limite >= 0.7).length;
  }, [month.extras, presupuestos]);

  // Progreso ACUMULADO de cada meta, de por vida:
  //   ahorrado_previo (años anteriores, lo calcula el backend)
  // + los aportes del año en curso, tomados del estado local
  //
  // Antes solo se sumaba `data`, que contiene el año en curso, así que cada
  // 1 de enero el progreso de todas las metas volvía a cero: una meta con
  // $80,000 ahorrados aparecía en $0. El dinero seguía en `ahorros_metas`;
  // mentía la barra.
  //
  // El año en curso se toma de `data` y no del backend para que la barra se
  // mueva al instante al editar el aporte del mes, antes de que el sync
  // confirme.
  const ahorradoPorMeta = useMemo(() => {
    const acc = {};
    for (const meta of metas) {
      acc[meta.id] = Number(meta.ahorrado_previo) || 0;
    }
    for (const m of Object.values(data)) {
      for (const a of (m.ahorroMetas || [])) {
        acc[a.metaId] = (acc[a.metaId] || 0) + (a.monto || 0);
      }
    }
    return acc;
  }, [metas, data]);

  // Solo bloquear la UI por monthLoading — global carga en background
  const loading = monthLoading;
  const error = monthError || globalError;

  const value = {
    // Raw data
    data, setData, month, setMonth, saveItem, mesId, mesIds,
    // Global entities
    metas, setMetas,
    presupuestos, setPresupuestos,
    recurrentes, setRecurrentes,
    msiCompras, setMsiCompras, toggleMsiPago,
    cuentas, createCuenta, updateCuenta, deleteCuenta,
    categorias, createCategoria, updateCategoria, deleteCategoria,
    transferencias, setTransferencias,
    diaCobro, setDiaCobro,
    nombreUsuario, setNombreUsuario,
    onboardingCompleted, setOnboardingCompleted,
    // Computed - saldos
    computedSaldos, saldoEfectivo, saldoDebito, saldoVales, saldoAhorro, capitalDisponible, capitalReal, hasCuentas, creditoIds,
    // Computed - month
    cuotasMes, totalMsiMes, totalIng, totalFij, totalExt, totalTDC,
    totalExtSinTDC, totalFijSinTDC, flujo, ahorroReal, flujoNeto,
    deudaTDC, totalDeudaTDCGlobal,
    // Computed - comprometido & flujo disponible
    comprometido, alertaTDC, flujoDisponible,
    // Computed - annual
    anualData, anualTotals,
    // Computed - charts & health
    pieData, saludToken, saludLabel, pctFlujo, alertCount, ahorradoPorMeta,
    // Time
    currentMonth, year, today,
    // State
    loading, error,
    refetchMonths, refetchGlobal, refetchTransferencias,
  };

  return (
    <FinancialContext.Provider value={value}>
      {children}
    </FinancialContext.Provider>
  );
}

export function useFinancial() {
  const ctx = useContext(FinancialContext);
  if (!ctx) throw new Error("useFinancial must be inside FinancialProvider");
  return ctx;
}
